import { useCallback } from 'react'
import { Minus, Plus } from 'lucide-react'
import { builderDurationStepperShellClassName } from './builderFieldStyles'

const DEFAULT_MIN = 5
const DEFAULT_STEP = 5
const DEFAULT_FALLBACK = 30

function snapMinutes(n, { min, step, max }) {
  if (!Number.isFinite(n)) return min
  const snapped = Math.round(n / step) * step
  let next = Math.max(min, snapped)
  if (max != null) next = Math.min(max, next)
  return next
}

function parseMinutesInput(raw, options) {
  const digits = String(raw ?? '').replace(/\D/g, '')
  if (!digits) return null
  return snapMinutes(Number(digits), options)
}

/**
 * Stepper for meeting duration in minutes (no native number spinners).
 */
export default function DurationMinutesStepper({
  id,
  value = '',
  onChange,
  min = DEFAULT_MIN,
  step = DEFAULT_STEP,
  max,
  disabled = false,
  hasError = false,
  required = false,
  label = 'Custom (minutes)',
  showLabel = true,
  size = 'default',
  onEnter,
  onAdjust,
}) {
  const compact = size === 'compact'
  const options = { min, step, max }
  const parsed = parseMinutesInput(value, options)

  const emit = useCallback(
    (next) => {
      onAdjust?.()
      if (next === '' || next == null) {
        onChange('')
        return
      }
      onChange(String(next))
    },
    [onAdjust, onChange]
  )

  const bump = useCallback(
    (delta) => {
      const base = parsed ?? snapMinutes(DEFAULT_FALLBACK, options)
      emit(snapMinutes(base + delta, options))
    },
    [emit, options, parsed]
  )

  const handleInputChange = (event) => {
    onAdjust?.()
    onChange(event.target.value.replace(/\D/g, ''))
  }

  const handleBlur = () => {
    if (String(value ?? '').trim() === '') return
    const digits = String(value ?? '').replace(/\D/g, '')
    if (!digits) return
    const n = Number(digits)
    if (!Number.isFinite(n)) return
    let clamped = Math.max(min, n)
    if (max != null) clamped = Math.min(max, clamped)
    emit(clamped)
  }

  const shellClass = [builderDurationStepperShellClassName(hasError), disabled ? 'opacity-60' : ''].join(' ')

  const stepButtonClass = [
    'flex shrink-0 items-center justify-center transition-all duration-200 ease-out',
    compact ? 'h-9 w-9 sm:h-10 sm:w-10' : 'h-12 w-12 sm:h-[3.25rem] sm:w-[3.25rem]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-1',
    disabled
      ? 'cursor-not-allowed bg-gray-50 text-gray-300'
      : 'bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800 active:scale-95',
  ].join(' ')

  const minusDisabled = disabled || (parsed != null && parsed <= min)
  const plusDisabled = disabled || (max != null && parsed != null && parsed >= max)

  return (
    <div className={compact ? 'w-full max-w-[12.5rem]' : 'w-full max-w-xs'}>
      {showLabel ? (
        <span
          id={id ? `${id}-label` : undefined}
          className={`mb-2 block font-medium text-gray-700 ${compact ? 'text-xs text-gray-600' : 'text-xs sm:text-sm'}`}
        >
          {label}
        </span>
      ) : null}
      <div className={shellClass} role="group" aria-labelledby={showLabel && id ? `${id}-label` : undefined}>
        <button
          type="button"
          disabled={minusDisabled}
          onClick={() => bump(-step)}
          className={`${stepButtonClass} border-r border-primary-100`}
          aria-label={`Decrease duration by ${step} minutes`}
        >
          <Minus className={compact ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6'} strokeWidth={2.5} />
        </button>

        <div
          className={`flex min-w-0 flex-1 flex-col items-center justify-center ${compact ? 'px-2 py-1.5' : 'px-3 py-2 sm:px-4'}`}
        >
          <input
            id={id}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                bump(step)
              } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                bump(-step)
              } else if (event.key === 'Enter' && onEnter) {
                event.preventDefault()
                onEnter(event)
              }
            }}
            placeholder={String(DEFAULT_FALLBACK)}
            className={`w-full min-w-[2.5rem] border-0 bg-transparent p-0 text-center font-semibold tabular-nums text-gray-900 outline-none placeholder:text-gray-300 focus:ring-0 disabled:cursor-not-allowed ${
              compact ? 'text-lg' : 'text-2xl sm:text-3xl'
            }`}
          />
          <span
            className={`font-medium uppercase tracking-wide text-primary-600 ${
              compact ? 'text-[0.6rem]' : 'mt-0.5 text-[0.65rem] sm:text-xs'
            }`}
          >
            {compact ? 'min' : 'minutes'}
          </span>
        </div>

        <button
          type="button"
          disabled={plusDisabled}
          onClick={() => bump(step)}
          className={`${stepButtonClass} border-l border-primary-100`}
          aria-label={`Increase duration by ${step} minutes`}
        >
          <Plus className={compact ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6'} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}
